// Third Party
import React, { ReactElement, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';

// Components
import { Button, Loader, Switch, Typography } from 'components';
import {
  ButtonBar,
  TutorialButton,
  useWidgetTableContext,
  WidgetTable
} from 'components/machine-health';

// Types
import { WidgetTableDataItem } from 'types/machine-health';
import { MachineHealthSubTabs, MachineHealthTabs, WidgetType } from 'types/protein';

// Providers
import { useLanguage } from 'providers';

// API
import {
  useGetMachineConfiguratorDataQuery,
  useUpdateMachineConfiguratorDataMutation
} from 'api';

// Constants
import { tutorialPlaceholderStepsData, WidgetTableColumnConfigs } from 'constants/machineConfig';

// Helpers
import { ToastMsg } from 'common/components/Toast';

// Hooks
import { useCopyConfiguration } from '../../hooks';

// Common
import CopyConfigurationActions from '../../components/CopyConfigurationActions';

// Styling
const Container = styled.div<{ removePadding?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${({ removePadding }) => (removePadding ? '0' : '0 6rem')};
  width: 100%;
`;

const WidgetTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 1.375rem;
  padding-bottom: 1.375rem;
  width: 100%;
`;

interface Props {
  copyWidgetType?: WidgetType;
  handleCopyCallback?: (item: WidgetTableDataItem) => void;
  hasWidgets?: boolean;
  isDirty: boolean;
  hideButtonRow?: boolean;
  isCopyTab?: boolean;
  overrideMachineId?: string;
  removePadding?: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const MachineConfigCleaning = ({
  copyWidgetType,
  handleCopyCallback,
  hasWidgets,
  hideButtonRow,
  isCopyTab,
  isDirty,
  overrideMachineId,
  removePadding,
  setIsDirty
}: Props): ReactElement => {
  const theme = useTheme();
  const { machineId } = useParams<{ machineId: string }>();
  const { languageId } = useLanguage();
  const {
    data,
    isFetching: fetching,
    error
  } = useGetMachineConfiguratorDataQuery({
    machineId: overrideMachineId || machineId,
    labels: [MachineHealthTabs.Cleaning],
    languageId: languageId,
    showInactive: true
  });

  const [updateProductCleaningData, { isLoading: updating, error: errorUpdate }] =
    useUpdateMachineConfiguratorDataMutation();

  const { localTableActive, localTableNames, localTableRows } = useWidgetTableContext();

  const [tabData, setTabData] = useState<WidgetTableDataItem | undefined>(data?.[0]);
  const [isActive, setIsActive] = useState(!!data?.[0]?.active);

  const {
    copiedConfiguration,
    copyModalOpen,
    copyFetching,
    updatingConfiguration,
    errorCopy,
    errorUpdateConfiguration,
    setCopyModalOpen,
    copyConfiguration,
    onCopyFromSelectedMachine,
    openCopyModal
  } = useCopyConfiguration({
    tabData,
    setTabData,
    setIsDirty,
    machineHealthTabLabel: MachineHealthTabs.Cleaning
  });

  const saveInfo = async () => {
    // Do nothing if we have no tab data
    if (!tabData) {
      return;
    }

    // Explicitly attach single session and steps over time since they do not have any widgets and
    // therefore are not included in the localTableRows
    const singleSession = tabData.members?.find(
      (member) => member.label === MachineHealthSubTabs.CLESingleSession
    );
    const stepsOverTime = tabData.members?.find(
      (member) => member.label === MachineHealthSubTabs.CLEStepsOverTime
    );

    const newMembers = tabData.members
      ?.map((member) => {
        if (member.label === MachineHealthSubTabs.CLESingleSession) {
          return {
            ...member,
            active: localTableActive.find((item) => item.id === singleSession?.id)?.active,
            name: localTableNames.find((name) => name.id === singleSession?.id)?.name
          };
        } else if (member.label === MachineHealthSubTabs.CLEStepsOverTime) {
          return {
            ...member,
            active: localTableActive.find((item) => item.id === stepsOverTime?.id)?.active,
            name: localTableNames.find((name) => name.id === stepsOverTime?.id)?.name
          };
        }
      })
      .filter((member) => !!member);

    // Reconstruct data to send to server
    const updatedCleaningTab = {
      ...tabData,
      active: isActive,
      members: [
        ...(newMembers || []),
        ...localTableRows
          .filter((row) => tabData.members?.some((tab) => tab.id === row.id))
          .map((row) => ({
            ...row,
            // Update the name and active status of each member(tab) with local state
            name: localTableNames.find((name) => name.id === row.id)?.name,
            active: localTableActive.find((active) => active.id === row.id)?.active
          }))
      ] as WidgetTableDataItem[]
    };

    await updateProductCleaningData({
      machineId,
      widget: updatedCleaningTab,
      languageId: languageId
    })
      .unwrap()
      .then(() => {
        ToastMsg({ heading: 'Success', type: 'success', message: `Cleaning updated successfully` });
      })
      .catch((error) => {
        ToastMsg({ heading: 'Error', type: 'error', message: `Failed to update cleaning` });
        console.error(error?.data?.detail);
      });

    setIsDirty(false);
  };

  useEffect(() => {
    setTabData(data?.[0]);
    setIsActive(!!data?.[0]?.active);
  }, [data]);

  useEffect(() => {
    setIsDirty(false);
  }, []);

  const handleEnableClick = () => {
    setIsActive((prev) => !prev);
    setIsDirty(true);
  };

  const isFetching = fetching || updating || copyFetching || updatingConfiguration;
  const isError = error || errorUpdate || errorCopy || errorUpdateConfiguration;

  if (isFetching) {
    return <Loader />;
  }

  if (isError) {
    console.error(isError);
    <Typography color="negativeRed">Failed to load data</Typography>;
  }

  return (
    <Container removePadding={removePadding}>
      {!hideButtonRow && (
        <ButtonBar columnGap="1.5rem" title="" padding="1.375rem 0">
          {tabData && !copiedConfiguration && (
            <>
              <Switch
                checked={isActive}
                handleDiameter={22}
                height={14}
                onChange={handleEnableClick}
                width={40}
                offColor={theme.colors.mediumGrey2}
                offHandleColor={theme.colors.mediumGrey3}
              />

              <Button
                disabled={!isDirty}
                onClick={() => saveInfo()}
                variant={'primary'}
                width="fit-content"
              >
                Save
              </Button>
            </>
          )}

          <CopyConfigurationActions
            copiedConfiguration={copiedConfiguration}
            copyConfiguration={copyConfiguration}
            copyModalOpen={copyModalOpen}
            hasWidgets={hasWidgets}
            onCopyFromSelectedMachine={onCopyFromSelectedMachine}
            openCopyModal={openCopyModal}
            setCopyModalOpen={setCopyModalOpen}
          />
          <TutorialButton stepsData={tutorialPlaceholderStepsData} />
        </ButtonBar>
      )}

      <WidgetTableContainer>
        {tabData?.members?.length ? (
          tabData.members.map((widgetTable) => (
            <WidgetTable
              columnConfigs={WidgetTableColumnConfigs}
              copyTable={{
                copyWidgetType,
                handleCopyCallback,
                // isCopyTable: false
                isCopyTable: isCopyTab
              }}
              data={widgetTable.members}
              isDataLoading={isFetching}
              key={widgetTable.id}
              parent={{ ...widgetTable, editable: true }}
              setIsDirty={setIsDirty}
              shouldRenderTags={false}
            />
          ))
        ) : (
          <Typography color="greyfont">No data found</Typography>
        )}
      </WidgetTableContainer>
    </Container>
  );
};

export default MachineConfigCleaning;
